'use client'

import * as React from 'react'

import type { ClientVariant, FallbackCookies } from '../types'
import { makeVariantVisible, selectVariant } from '../scripts/head-scripts'
import { VariantSelectorScript } from '../scripts/body-scripts'
import { StaticVariant } from './static-variant'
import {
  isClientSideVariantSelection,
  isEdgeVariantSelection,
  isHtmlRenderVariantSelection,
  resetToClientSideVariantSelection,
} from '../utils/rendering-selection-state'
import { convertToLean } from '../utils/convert-to-lean'

resetToClientSideVariantSelection()

function StaticVariantsRendererClientInternal({
  variants,
  fallbackCookies = {},
  renderingId,
}: {
  variants: ClientVariant[]
  fallbackCookies?: FallbackCookies
  renderingId: string
}) {
  const hasMountedRef = React.useRef(false)

  // useInsertionEffect runs synchronously before the browser paints, ensuring
  // the visibility style is in the DOM before the user sees a frame without it.
  // This prevents the flash that occurs when hydration produces new renderingIds
  // that don't match the SSR-injected style tags.
  React.useInsertionEffect(() => {
    if (
      (isClientSideVariantSelection() || isHtmlRenderVariantSelection()) &&
      !hasMountedRef.current
    ) {
      const variantId = selectVariant({ variants: convertToLean(variants), fallbackCookies })
      makeVariantVisible(variantId, renderingId)
    }
    hasMountedRef.current = true
  }, [renderingId, variants, fallbackCookies])

  const staticVariantsData = React.useMemo(
    () => ({
      variants: convertToLean(variants),
      renderingId,
      fallbackCookies,
    }),
    [variants, renderingId, fallbackCookies],
  )

  return (
    <span
      id={renderingId}
      // oxlint-disable-next-line no-negated-condition
      className={!isEdgeVariantSelection() ? 'static-variant-renderer' : undefined}
      suppressHydrationWarning={true}
      data-variants-data={
        // oxlint-disable-next-line no-negated-condition
        !isEdgeVariantSelection() ? JSON.stringify(staticVariantsData) : undefined
      }
    >
      {variants.map(variant => (
        <StaticVariant key={variant.id} variant={variant} renderingId={renderingId} />
      ))}

      <VariantSelectorScript renderingId={renderingId} />
      <SwitchBackToClientSideVariantSelection />
    </span>
  )
}

function SwitchBackToClientSideVariantSelection() {
  React.useEffect(() => {
    resetToClientSideVariantSelection()
  }, [])
  return null
}

export const StaticVariantsRendererClient = React.memo(StaticVariantsRendererClientInternal)
